import type { DiscoveredIp } from "../shared/utils/discovery";
import { http } from "../services/http";

// Mirrors GrantStatus and DelegatedIP in common/api/grant_status.go.
export interface DelegatedIP {
  IP: string;
  Status: number;
  // "delegated_config", "delegated_link" or "agentic_access"; agentic IPs show
  // in the agentic panel.
  Source?: string;
  // The agentic access name, shown in a tooltip.
  Name?: string;
  // RFC3339; absent when the grant has no explicit expiry.
  ExpiresAt?: string | null;
}

// What a trust policy did at one address of the session.
export type TrustLimitedOutcome = "refused" | "held" | "capped" | "click";

export interface TrustLimitedAddress {
  IP: string;
  Outcome: TrustLimitedOutcome;
  // The cap on the grant at this address, in seconds, for a capped outcome.
  CapSeconds?: number;
}

export interface ACL {
  ACLID?: string;
  URL: string;
  Description?: string;
  Category?: string;
  Icon?: string;
  Status: number;
  ClickToGrant?: boolean;
  // A trust policy wants the user to verify before this knoc grants.
  TrustChallengeRequired?: boolean;
  // The server is still checking the connection; a later poll settles it.
  TrustPending?: boolean;
  // Polled through getGrantStates until a device pulls the allowlist.
  PublishedGrantIDs?: string[];
  // Set when a passed trust challenge capped the grant below the knoc's duration.
  TrustGrantCapSeconds?: number;
  // The addresses of this session a trust policy limited for this knoc, and
  // what it did at each: the browser's own when the fields above say so, a
  // discovered one when its own grant was refused, held or capped.
  TrustLimitedAddresses?: TrustLimitedAddress[];
  RequireTicketNumber?: boolean;
  RequireTOTP?: boolean;
  CGNATOpen?: boolean;
  HideFromUsers?: boolean;
  DisableForUser?: boolean;
  AutoBrowseUsers?: boolean;
  InstantReferrerRedirect?: boolean;
  GrantTypeFixedIps?: boolean;
  ExpiredAfter?: string;
  ParentID?: string;
  Error?: string;
  Config?: {
    UriToken?: string;
  };
  PropagationDelayMs?: number;
  // null is the portal default; 0 is never wait.
  MaxRedirectWaitMs?: number | null;
  PersistentAndAgenticEnabled?: boolean;
  PersistentIPs?: DelegatedIP[];
  // The predefined sources a fixed-IPs knoc grants, each named by the network
  // it came from ("" for an inline source).
  GrantSources?: GrantSource[];
  // "user" lets the user pick which sources a click grants.
  GrantSourceSelection?: string;
}

export interface GrantSource {
  Source: string;
  Name: string;
  NetworkId: string;
}

// The country a provider reported for one address: the two-letter code and the
// name as the provider spelled it, when it gave one.
export interface AddressPlacement {
  CountryCode: string;
  Country?: string;
}

export interface User {
  ACLs?: ACL[];
  // What the browser's port walk found this session, besides CurrentAddress.
  DiscoveredAddresses?: string[];
  // Where a provider told the trust engine each address of the session is,
  // keyed by address: the country the policy judged. An address no provider
  // placed is absent.
  AddressPlacements?: Record<string, AddressPlacement>;
  AllowAgenticAccess?: boolean;
  AllowPersistentIPs?: boolean;
  // Shown when the user matches no access paths.
  SamlGroups?: string[];
  // Global flag; false hides the delegated and agentic panels.
  PersistentAndAgenticEnabled?: boolean;
  // False when the session-slot pool is full, which holds the user on the session-limit screen.
  HasSessionSlot?: boolean;
  // An admin set this user's password, which holds them on the change-password screen.
  PasswordChangeRequired?: boolean;
  // This browser signed in to the admin portal and has not signed out.
  // ServerUpdateAvailable rides on it and carries no version.
  AdminPortalUser?: boolean;
  ServerUpdateAvailable?: boolean;
  // AdminPortalURL is set only when the admin portal has its own URL.
  AdminPortalURL?: string;
  [key: string]: any;
}

export interface GrantState {
  ID: string;
  Status: number;
}

// Answers only this session's grants.
export async function getGrantStates(ids: readonly string[]): Promise<GrantState[]> {
  const body = await http.get<{ Grants: GrantState[] }>("/sessions/current/grant-states", {
    params: { ids: ids.join(",") },
  });
  return body.Grants ?? [];
}

// A Knocproxy knoc's token goes on the tile's URL.
export async function getUserStatus(options: { signal?: AbortSignal } = {}): Promise<User> {
  const user = await http.get<User>("/status", options);
  if (!user.ACLs) user.ACLs = [];
  user.ACLs.forEach((acl: ACL) => {
    if (acl.Config?.UriToken) {
      const url = new URL(acl.URL);
      url.searchParams.set("knoctoken", acl.Config.UriToken);
      acl.URL = url.toString();
    }
  });
  return user;
}

export interface GrantAclInput {
  aclId: string;
  ips: DiscoveredIp[];
  ticketNumber?: string;
  totpCode?: string;
  // Narrows a knoc that lets the user choose. Omitted grants every source.
  sources?: string[];
}

export function grantAcl(input: GrantAclInput): Promise<void> {
  return http.post("/grant", {
    ACLID: input.aclId,
    IPs: input.ips,
    TicketNumber: input.ticketNumber || null,
    TOTPCode: input.totpCode || null,
    ...(input.sources?.length ? { Sources: input.sources } : {}),
  });
}

export function revokeAcl(aclId: string): Promise<void> {
  return http.delete(`/sessions/current/granted-acls/${encodeURIComponent(aclId)}`);
}

export interface Theme {
  Accent: string;
  HighAccessibility: boolean;
  [field: string]: unknown;
}

// Unauthenticated, for the login screens.
export function getTheme(options: { signal?: AbortSignal } = {}): Promise<Theme> {
  return http.get<Theme>("/theme", options);
}

// Logout aborts this so it cannot land afterwards and re-grant an address.
export function submitDiscoveredIps(ips: DiscoveredIp[], options: { signal?: AbortSignal } = {}): Promise<void> {
  return http.post("/discovered-ips", { IPs: ips }, options);
}
